import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { Card, Grid, Stack, styled, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getIn, useFormik } from 'formik';
import type { UserProfileDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  CountrySelector,
  FTextInput,
  LoadingPromiseButtonAdvanced,
} from '@pflegenavi/web-components';
import type { ServiceProviderCompanyDto } from '@pflegenavi/shared/api';
import { PHONE_NUMBER_REGEX } from '@pflegenavi/shared/constants';
import { useGetAllCountriesPhoenix } from '@pflegenavi/service-provider-frontend/api';
import { keepPreviousData } from '@tanstack/react-query';

export interface IProfileFormValues {
  companyName: string | undefined;
  address: string | undefined;
  zipCode: string | undefined;
  city: string | undefined;
  phone: string | undefined;
  countryId: string | undefined;
}

interface ServiceProviderProfileInfoProps {
  userProfile: UserProfileDto;
  company: ServiceProviderCompanyDto;
  handleUpdateServiceProvider: (values: IProfileFormValues) => Promise<void>;
}

export const ServiceProviderProfileInfo: FC<
  ServiceProviderProfileInfoProps
> = ({
  userProfile: {
    user: { firstName, lastName, eMail },
  },
  company: { name: companyName, address, zipCode, city, phone, countryId },
  handleUpdateServiceProvider,
}) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(() => {
    return Yup.object({
      companyName: Yup.string()
        .min(
          2,
          t('service-provider-app.connect-account.form.valid.company-name')
        )
        .max(
          255,
          t('service-provider-app.connect-account.form.valid.company-name')
        )
        .required(
          t('service-provider-app.connect-account.form.required.company-name')
        ),
      address: Yup.string()
        .min(4, t('service-provider-app.connect-account.form.valid.address'))
        .max(255, t('service-provider-app.connect-account.form.valid.address'))
        .required(
          t('service-provider-app.connect-account.form.required.address')
        ),
      zipCode: Yup.string()
        .min(3, t('service-provider-app.connect-account.form.valid.zip-code'))
        .max(9, t('service-provider-app.connect-account.form.valid.zip-code'))
        .required(
          t('service-provider-app.connect-account.form.required.zip-code')
        ),
      city: Yup.string()
        .min(2, t('service-provider-app.connect-account.form.valid.city'))
        .max(255, t('service-provider-app.connect-account.form.valid.city'))
        .required(t('service-provider-app.connect-account.form.required.city')),
      countryId: Yup.string().optional(),
      phone: Yup.string()
        .matches(PHONE_NUMBER_REGEX, {
          message: t('service-provider-app.connect-account.form.valid.phone'),
        })
        .required(
          t('service-provider-app.connect-account.form.required.phone')
        ),
    });
  }, [t]);

  const initialValues: IProfileFormValues = useMemo(() => {
    return {
      companyName,
      address,
      zipCode,
      city,
      phone,
      countryId,
    };
  }, [address, city, companyName, phone, zipCode, countryId]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleUpdateServiceProvider,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  // This pretty much never changes
  const { data: countries } = useGetAllCountriesPhoenix({
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.countryId === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        countryId: newCountryId,
      }));
      formik.setFieldError('countryId', undefined);
    },
    [formik]
  );

  const onBlur = useCallback(() => {
    formik.setFieldTouched('countryId', true);
  }, [formik]);

  return (
    <ContainerCard>
      <Stack direction="row" gap={2} sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              id="firstName"
              name="firstName"
              value={firstName}
              disabled
              label={t('service-provider-app.connect-account.form.first-name')}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              id="lastName"
              name="lastName"
              value={lastName}
              label={t('service-provider-app.connect-account.form.last-name')}
              disabled
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FTextInput
              id="companyName"
              name="companyName"
              label={t(
                'service-provider-app.connect-account.form.company-name'
              )}
              formik={formik}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FTextInput
              id="address"
              name="address"
              label={t('service-provider-app.connect-account.form.address')}
              formik={formik}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FTextInput
              id="zipCode"
              name="zipCode"
              label={t('service-provider-app.connect-account.form.zip-code')}
              formik={formik}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FTextInput
              id="city"
              name="city"
              label={t('service-provider-app.connect-account.form.city')}
              formik={formik}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <CountrySelector
              disabled={true}
              onChangeCountry={onChangeCountry}
              countries={countries ?? []}
              countryId={formik.values.countryId}
              onBlur={onBlur}
              errorMessage={
                getIn(formik.touched, 'countryId') &&
                getIn(formik.errors, 'countryId')
              }
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="eMail"
              name="eMail"
              value={eMail}
              disabled
              label={t('service-provider-app.profile.email')}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FTextInput
              id="phone"
              type="tel"
              name="phone"
              label={t('service-provider-app.connect-account.form.phone')}
              formik={formik}
              fullWidth
            />
          </Grid>
        </Grid>
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <LoadingPromiseButtonAdvanced
          variant="contained"
          onClick={formik.submitForm}
          disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
        >
          {t('service-provider-app.profile.save-changes')}
        </LoadingPromiseButtonAdvanced>
      </Stack>
    </ContainerCard>
  );
};

const ContainerCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    flex: 1,
    p: 3,
  })
);
